import { useCallback, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { OrderSelectOrCreate } from '@/app/order/workflow/OrderSelectOrCreate'
import { trpc } from '@klear/ui/lib/trpc'
import { operationalToStoredEventData } from '@klear/operational/wire.client'
import { ROUTE_OPERATIONAL, ROUTE_ORDERS, ROUTE_PAYABLE, buildNavPathTrader } from '@klear/constants'
import { Wizard } from '@klear/ui/wizard/wizard'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import {
  enhanceReturnUrl,
  returnUrlHasPathSegment,
  useNavigateWithReturnUrl,
  useReturnUrlExit
} from '@klear/ui/wizard/return-url'
import { useWizardDirtyLeaveGuard } from '@klear/ui/wizard/dirty-leave-guard'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'
import toInteger from 'lodash/toInteger'
import toString from 'lodash/toString'
import isFinite from 'lodash/isFinite'
import { OperationalFlow } from '@/app/operational/workflow/operational-step-group'
import { positiveIntFromQuery } from '@/app/workflow/wizard-url-seed'
import { STEP_ID } from '@klear/payable/constants'

export type OperationalWizardPageMode = 'create' | 'edit'

export default function OperationalWizardPage({ mode }: { mode: OperationalWizardPageMode }) {
  const { traderId, operationalId: operationalIdParam } = useParams<{
    traderId?: string
    operationalId?: string
  }>()
  const [searchParams] = useSearchParams()
  const tid = toInteger(traderId)
  const seededOrderId = positiveIntFromQuery(searchParams.get('orderId'))
  const [pickedOrderId, setPickedOrderId] = useState<number | null>(null)
  const { navigateWithReturnUrl } = useNavigateWithReturnUrl()
  const orderId = seededOrderId ?? pickedOrderId
  const parentEventId = positiveIntFromQuery(searchParams.get('parentEventId'))
  const supplierIdSeed = mode === 'create' ? positiveIntFromQuery(searchParams.get('supplierId')) : null
  const existingIdRaw = operationalIdParam ? toInteger(operationalIdParam) : NaN
  const existingId = mode === 'edit' && isFinite(existingIdRaw) && existingIdRaw > 0 ? existingIdRaw : null

  const defaultExit = buildNavPathTrader(toString(tid), ROUTE_ORDERS)
  const { exit, exitAfterSave } = useReturnUrlExit(defaultExit)
  const [nav, setNav] = useState<WizardStepGroupNavState | null>(null)
  const [dirty, setDirty] = useState(false)
  const { LeaveConfirmDialog, guardExit } = useWizardDirtyLeaveGuard({ when: dirty })
  const guardedExit = useMemo(() => guardExit(exit), [guardExit, exit])

  const onComplete = useCallback(
    ({ operationalId }: { operationalId: number }) => {
      exitAfterSave((ret) => {
        const query = { operationalId: toString(operationalId) }
        if (returnUrlHasPathSegment(ret, ROUTE_PAYABLE)) {
          return enhanceReturnUrl(ret, { query, hash: STEP_ID.OPERATIONAL })
        }
        return enhanceReturnUrl(ret, { query })
      })
    },
    [exitAfterSave]
  )

  const { data: existingOperational } = trpc.operational.get.useQuery(
    { id: existingId ?? 0, traderId: tid },
    { enabled: mode === 'edit' && existingId != null && existingId > 0 }
  )
  const initialData = useMemo(
    () => (existingOperational != null ? operationalToStoredEventData(existingOperational) : null),
    [existingOperational]
  )
  useDocumentTitle(existingOperational?.title, mode === 'edit' ? 'Edit Operational' : 'New Operational')

  if (orderId == null) {
    if (mode === 'edit') {
      return <div className="p-6 text-sm text-muted-foreground">Missing orderId for operational {mode}.</div>
    }
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <Wizard name="Operational" subtitle="Trade" steps={[]} activeStepId="" onBack={exit} backLabel="Exit">
          <OrderSelectOrCreate
            traderId={tid}
            onSelected={setPickedOrderId}
            onCreateNew={() => navigateWithReturnUrl(buildNavPathTrader(toString(tid), ROUTE_ORDERS, 'new'))}
          />
        </Wizard>
      </div>
    )
  }
  if (mode === 'edit' && existingId == null) {
    return <div className="p-6 text-sm text-muted-foreground">Missing operational id.</div>
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {LeaveConfirmDialog}
      <Wizard
        name="Operational"
        subtitle={nav?.steps.find((s) => s.id === nav.activeStepId)?.label ?? (mode === 'edit' ? 'Edit' : 'Create')}
        steps={nav?.steps ?? []}
        activeStepId={nav?.activeStepId ?? ''}
        onStepSelect={nav?.onStepSelect}
        isStepDisabled={nav?.isStepDisabled}
        onBack={guardedExit}
        backLabel="Exit"
      >
        <OperationalFlow
          mode={mode}
          traderId={tid}
          orderId={orderId}
          parentEventId={parentEventId}
          supplierIdSeed={supplierIdSeed}
          existingId={existingId}
          initialData={initialData}
          cancelOnFirstStep
          onNavStateChange={setNav}
          onComplete={onComplete}
          onCancel={guardedExit}
          onDirtyChange={setDirty}
        />
      </Wizard>
    </div>
  )
}

export function operationalCreatePath(
  traderId: string | number,
  orderId: number | null,
  seed: { parentEventId?: number | null; supplierId?: number | null } = {}
): string {
  const qs = new URLSearchParams(orderId != null && orderId > 0 ? { orderId: toString(orderId) } : {})
  if (seed.parentEventId != null && seed.parentEventId > 0) qs.set('parentEventId', toString(seed.parentEventId))
  if (seed.supplierId != null && seed.supplierId > 0) qs.set('supplierId', toString(seed.supplierId))
  return `${buildNavPathTrader(toString(traderId), ROUTE_OPERATIONAL, 'new')}?${qs.toString()}`
}

export function operationalEditPath(
  traderId: string | number,
  operationalId: number,
  orderId: number,
  parentEventId?: number | null
): string {
  const qs = new URLSearchParams({ orderId: toString(orderId) })
  if (parentEventId != null && parentEventId > 0) qs.set('parentEventId', toString(parentEventId))
  return `${buildNavPathTrader(toString(traderId), ROUTE_OPERATIONAL, toString(operationalId), 'edit')}?${qs.toString()}`
}
